import React, { useEffect, useMemo, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Mention from "@tiptap/extension-mention";
import CharacterCount from "@tiptap/extension-character-count";
import Placeholder from "@tiptap/extension-placeholder";
import EmojiPicker from "emoji-picker-react";
import { HASHTAG_SUGGESTIONS } from "./SocialTextInputs";
import "./CompactPostEditor.css";

const allowedTags = new Set(["A", "B", "BLOCKQUOTE", "BR", "DIV", "EM", "H1", "H2", "H3", "I", "LI", "OL", "P", "STRONG", "U", "UL"]);

export const sanitizeEditorHtml = (html) => {
  const parsed = new DOMParser().parseFromString(String(html || ""), "text/html");
  const cleanNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) return document.createTextNode(node.textContent || "");
    if (node.nodeType !== Node.ELEMENT_NODE) return document.createDocumentFragment();
    if (["SCRIPT", "STYLE", "IFRAME", "OBJECT", "SVG", "MATH"].includes(node.tagName)) return document.createDocumentFragment();
    const children = [...node.childNodes].map(cleanNode);
    if (!allowedTags.has(node.tagName)) {
      const fragment = document.createDocumentFragment();
      children.forEach((child) => fragment.append(child));
      return fragment;
    }
    const element = document.createElement(node.tagName.toLowerCase());
    if (node.tagName === "A") {
      try {
        const url = new URL(node.getAttribute("href") || "");
        if (!["http:", "https:"].includes(url.protocol)) throw new Error("Unsupported link protocol");
        element.setAttribute("href", url.href);
      } catch {
        const fragment = document.createDocumentFragment();
        children.forEach((child) => fragment.append(child));
        return fragment;
      }
    }
    children.forEach((child) => element.append(child));
    return element;
  };
  const container = document.createElement("div");
  [...parsed.body.childNodes].map(cleanNode).forEach((node) => container.append(node));
  return container.innerHTML;
};

export const editorHtmlToText = (html) => {
  const parsed = new DOMParser().parseFromString(String(html || ""), "text/html");
  const nodeText = (node) => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent || "";
    if (node.nodeType !== Node.ELEMENT_NODE) return "";
    if (node.tagName === "BR") return "\n";
    const content = [...node.childNodes].map(nodeText).join("");
    if (node.tagName === "A") {
      try {
        const url = new URL(node.getAttribute("href") || "");
        if (["http:", "https:"].includes(url.protocol) && !content.includes(url.href)) return `${content} ${url.href}`;
      } catch {
        return content;
      }
    }
    return ["DIV", "LI", "P", "BLOCKQUOTE", "H1", "H2", "H3"].includes(node.tagName) ? `${content}\n` : content;
  };
  return [...parsed.body.childNodes].map(nodeText).join("").replace(/\u00a0/g, " ").replace(/\n{3,}/g, "\n\n").trim();
};

const makeHashtagSuggestions = () => {
  let menu;
  let selectedIndex = 0;
  let currentItems = [];
  let currentCommand;

  const removeMenu = () => {
    menu?.remove();
    menu = null;
    currentItems = [];
    currentCommand = null;
  };

  const updateMenu = (props) => {
    currentItems = props.items;
    currentCommand = props.command;
    selectedIndex = Math.min(selectedIndex, Math.max(currentItems.length - 1, 0));
    if (!menu) {
      menu = document.createElement("div");
      menu.className = "tiptap-hashtag-suggestions";
      menu.setAttribute("role", "listbox");
      menu.setAttribute("aria-label", "Suggested hashtags");
      document.body.append(menu);
    }
    menu.replaceChildren();
    currentItems.forEach((item, index) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "tiptap-hashtag-suggestion";
      button.setAttribute("role", "option");
      button.setAttribute("aria-selected", String(index === selectedIndex));
      const hash = document.createElement("span");
      hash.textContent = "#";
      const label = document.createElement("span");
      label.textContent = item.label;
      button.append(hash, label);
      button.addEventListener("pointerdown", (event) => event.preventDefault());
      button.addEventListener("click", () => currentCommand?.(item));
      menu.append(button);
    });
    const rect = props.clientRect?.();
    if (rect && menu) {
      menu.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - 240))}px`;
      menu.style.top = `${Math.min(rect.bottom + 6, window.innerHeight - 12)}px`;
    }
  };

  return {
    onStart: updateMenu,
    onUpdate: updateMenu,
    onKeyDown: ({ event }) => {
      if (event.key === "ArrowDown") {
        if (!currentItems.length) return false;
        selectedIndex = (selectedIndex + 1) % currentItems.length;
        menu?.querySelectorAll("button").forEach((button, index) => button.setAttribute("aria-selected", String(index === selectedIndex)));
        return true;
      }
      if (event.key === "ArrowUp") {
        if (!currentItems.length) return false;
        selectedIndex = (selectedIndex - 1 + currentItems.length) % currentItems.length;
        menu?.querySelectorAll("button").forEach((button, index) => button.setAttribute("aria-selected", String(index === selectedIndex)));
        return true;
      }
      if (event.key === "Enter" && currentItems[selectedIndex]) {
        currentCommand?.(currentItems[selectedIndex]);
        return true;
      }
      if (event.key === "Escape") {
        removeMenu();
        return true;
      }
      return false;
    },
    onExit: removeMenu
  };
};

const hashtagExtension = Mention.extend({
  name: "hashtag",
  addOptions() {
    return {
      ...this.parent?.(),
      HTMLAttributes: { class: "post-hashtag" },
      suggestion: {}
    };
  },
  renderText({ node }) {
    return `#${node.attrs.label || node.attrs.id}`;
  },
  renderHTML({ node, HTMLAttributes }) {
    return ["span", { ...HTMLAttributes, "data-type": "hashtag" }, `#${node.attrs.label || node.attrs.id}`];
  },
}).configure({
  suggestions: [{
    char: "#",
    allowSpaces: false,
    items: ({ query }) => HASHTAG_SUGGESTIONS
      .filter((tag) => tag.startsWith(query.toLowerCase()))
      .slice(0, 6)
      .map((tag) => ({ id: tag, label: tag })),
    render: makeHashtagSuggestions
  }]
});

const CompactPostEditor = ({ value = "", onChange, maxLength = 3000 }) => {
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const onChangeRef = useRef(onChange);
  const extensions = useMemo(() => [
    StarterKit.configure({
      heading: { levels: [1, 2, 3] },
      link: false
    }),
    Link.configure({
      openOnClick: false,
      autolink: true,
      defaultProtocol: "https",
      protocols: ["http", "https"],
      HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" }
    }),
    Underline,
    Placeholder.configure({ placeholder: "Write a post description..." }),
    hashtagExtension,
    CharacterCount.configure({ limit: maxLength })
  ], [maxLength]);

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  const editor = useEditor({
    extensions,
    content: sanitizeEditorHtml(value),
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: "compact-editor-content",
        "aria-label": "Description",
        "aria-multiline": "true"
      },
      handlePaste(view, event) {
        const text = event.clipboardData?.getData("text/plain");
        if (text === undefined) return false;
        event.preventDefault();
        view.dispatch(view.state.tr.insertText(text));
        return true;
      }
    },
    onUpdate: ({ editor: currentEditor }) => {
      onChangeRef.current(sanitizeEditorHtml(currentEditor.getHTML()));
    }
  });

  useEffect(() => {
    if (!editor) return;
    editor.view.dom.classList.add("compact-editor-content");
    editor.view.dom.setAttribute("role", "textbox");
    editor.view.dom.setAttribute("aria-label", "Description");
    editor.view.dom.setAttribute("aria-multiline", "true");
  }, [editor]);

  useEffect(() => {
    if (!editor) return;
    const safeValue = sanitizeEditorHtml(value);
    if (sanitizeEditorHtml(editor.getHTML()) !== safeValue) editor.commands.setContent(safeValue, { emitUpdate: false });
  }, [editor, value]);

  const characterCount = editor?.storage.characterCount.characters() || 0;

  const insertLink = (event) => {
    event.preventDefault();
    const rawUrl = linkUrl.trim();
    const normalizedUrl = /^[a-z][a-z\d+.-]*:/i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;
    try {
      const url = new URL(normalizedUrl);
      if (!["http:", "https:"].includes(url.protocol)) return;
      const selection = editor.state.selection;
      if (selection.empty) {
        editor.chain().focus().insertContent({
          type: "text",
          text: url.href,
          marks: [{ type: "link", attrs: { href: url.href, target: "_blank", rel: "noopener noreferrer" } }]
        }).run();
      } else {
        editor.chain().focus().setLink({ href: url.href, target: "_blank", rel: "noopener noreferrer" }).run();
      }
      setLinkOpen(false);
      setLinkUrl("");
    } catch {
      return;
    }
  };

  if (!editor) return <div className="compact-post-editor compact-editor-loading" aria-label="Loading rich text editor" />;

  const preserveEditorSelection = (event) => event.preventDefault();

  return <div className="compact-post-editor">
    <div className="compact-editor-toolbar" role="toolbar" aria-label="Description formatting">
      <button type="button" className={editor.isActive("bold") ? "is-active" : ""} aria-label="Bold" title="Bold" onMouseDown={preserveEditorSelection} onClick={() => editor.chain().focus().toggleBold().run()}><strong>B</strong></button>
      <button type="button" className={editor.isActive("italic") ? "is-active" : ""} aria-label="Italic" title="Italic" onMouseDown={preserveEditorSelection} onClick={() => editor.chain().focus().toggleItalic().run()}><em>I</em></button>
      <button type="button" className={editor.isActive("underline") ? "is-active" : ""} aria-label="Underline" title="Underline" onMouseDown={preserveEditorSelection} onClick={() => editor.chain().focus().toggleUnderline().run()}><u>U</u></button>
      <span className="compact-editor-divider" />
      <button type="button" className={editor.isActive("bulletList") ? "is-active" : ""} aria-label="Bulleted list" title="Bulleted list" onMouseDown={preserveEditorSelection} onClick={() => editor.chain().focus().toggleBulletList().run()}><i className="ti ti-list" /></button>
      <button type="button" className={editor.isActive("orderedList") ? "is-active" : ""} aria-label="Numbered list" title="Numbered list" onMouseDown={preserveEditorSelection} onClick={() => editor.chain().focus().toggleOrderedList().run()}><i className="ti ti-list-numbers" /></button>
      <button type="button" className={linkOpen || editor.isActive("link") ? "is-active" : ""} aria-label="Insert link" title="Insert link" aria-expanded={linkOpen} onMouseDown={preserveEditorSelection} onClick={() => setLinkOpen((open) => !open)}><i className="ti ti-link" /></button>
      <button type="button" aria-label="Insert hashtag" title="Insert hashtag" onMouseDown={preserveEditorSelection} onClick={() => editor.chain().focus().insertContent("#").run()}><strong>#</strong></button>
      <button type="button" className={emojiOpen ? "is-active" : ""} aria-label="Open emoji picker" title="Add emoji" aria-expanded={emojiOpen} onMouseDown={preserveEditorSelection} onClick={() => setEmojiOpen((open) => !open)}>😊</button>
    </div>
    {linkOpen && <form className="compact-link-form" onSubmit={insertLink}>
      <input className="form-control" type="text" inputMode="url" aria-label="Link URL" placeholder="https://example.com" value={linkUrl} onChange={(event) => setLinkUrl(event.target.value)} required />
      <button className="btn btn-primary" type="submit">Insert</button>
    </form>}
    <div className="compact-editor-body">
      <EditorContent editor={editor} />
      <span className={`compact-editor-count${characterCount >= maxLength ? " is-limit" : ""}`}>{characterCount}/{maxLength}</span>
      {emojiOpen && <div className="compact-emoji-picker">
        <EmojiPicker
          onEmojiClick={({ emoji }) => editor.chain().focus().insertContent(emoji).run()}
          width="100%"
          height={310}
          previewConfig={{ showPreview: false }}
          lazyLoadEmojis
        />
      </div>}
    </div>
    <small className="compact-editor-hint">Format your post, add hashtags and insert links or emojis.</small>
  </div>;
};

export default CompactPostEditor;
